/* eslint-disable camelcase */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import prebid from "prebid.js";
import "prebid.js/modules/consentManagement";
import "prebid.js/modules/currency";
import "prebid.js/modules/rubiconBidAdapter";
import "prebid.js/modules/rubiconAnalyticsAdapter";
import "prebid.js/modules/adformBidAdapter";
import "prebid.js/modules/userId";
import "prebid.js/modules/pubCommonIdSystem";
import Service from "../service";
var PbjsConsentApi;
(function (PbjsConsentApi) {
    PbjsConsentApi["IAB"] = "iab";
    PbjsConsentApi["Static"] = "static";
})(PbjsConsentApi || (PbjsConsentApi = {}));
const defaultConf = {
    enabled: true,
    defaults: {
        prebidTimeout: 500,
        consentTimeout: 500,
        granularity: "dense",
        rubicon: {
            singleRequest: true,
        },
        adform: {
            rcur: "USD",
        },
    },
};
var PrebidVideoContext;
(function (PrebidVideoContext) {
    PrebidVideoContext["Outstream"] = "outstream";
    PrebidVideoContext["Instream"] = "instream";
    PrebidVideoContext["AdPod"] = "adpod";
})(PrebidVideoContext || (PrebidVideoContext = {}));
var AdPodContentMode;
(function (AdPodContentMode) {
    AdPodContentMode["Live"] = "live";
    AdPodContentMode["OnDemand"] = "on-demand";
})(AdPodContentMode || (AdPodContentMode = {}));
var PrebidNativeType;
(function (PrebidNativeType) {
    PrebidNativeType["Image"] = "image";
})(PrebidNativeType || (PrebidNativeType = {}));
class PrebidService extends Service {
    constructor(config, context) {
        super("Prebid", context);
        this.configuration = { ...defaultConf, ...config };
        this.prebidInstance = prebid;
        prebid.processQueue();
        this.configuration.defaults = {
            ...defaultConf.defaults,
            ...config.defaults,
        };
        this.logger.logDebug(`Prebid config: ${JSON.stringify(this.configuration, null, 2)}`);
        this.logger.logInfo(`Using pbjs ${prebid.version}`);
    }
    buildAdUnits(placements) {
        this.logger.logInfo(`Building adunits for Prebid...`);
        const adUnits = [];
        placements.forEach((p) => {
            if (p.prebid && p.prebid.active === true) {
                const prebidTarget = this.configuration.bids[p.prebid.source];
                if (!prebidTarget) {
                    this.logger.logWarn(`No prebid target found for source ${p.prebid.source}`);
                }
                else {
                    const adUnit = {
                        code: p.divId,
                        mediaTypes: { banner: { sizes: p.sizes } },
                        bids: [],
                    };
                    prebidTarget.bidders.forEach((bidConfig) => {
                        switch (bidConfig.bidder) {
                            case "rubicon": {
                                this.logger.logInfo(`Found bidder Rubicon for ad unit ${adUnit.code}`);
                                const siteId = bidConfig.params.siteId ??
                                    this.configuration.defaults?.rubicon?.siteId;
                                const accountId = this.configuration.defaults?.rubicon
                                    ?.accountId;
                                if (siteId && accountId) {
                                    adUnit.bids.push({
                                        bidder: "rubicon",
                                        params: {
                                            zoneId: bidConfig.params.zoneId,
                                            siteId,
                                            accountId,
                                            ...p.prebid?.rubicon?.params,
                                        },
                                    });
                                }
                                else {
                                    this.logger.logError(`Missing site or account id for bid ${bidConfig}`);
                                }
                                break;
                            }
                            case "adform": {
                                this.logger.logInfo(`Found bidder Adform for ad unit ${adUnit.code}`);
                                const { mid } = bidConfig.params;
                                const rcur = this.configuration.defaults?.adform?.rcur;
                                if (mid && rcur) {
                                    adUnit.bids.push({
                                        bidder: "adform",
                                        params: {
                                            mid,
                                            rcur,
                                            ...p.prebid?.adform?.params,
                                        },
                                    });
                                }
                                else {
                                    this.logger.logError(`Missing site or account id for bid ${bidConfig}`);
                                }
                                break;
                            }
                            default:
                                this.logger.logError("Invalid bidder");
                        }
                    });
                    adUnits.push(adUnit);
                }
            }
        });
        this.logger.logDebug(`Built adunits: ${JSON.stringify(adUnits, null, 2)}`);
        return adUnits;
    }
    resetPrebidState() {
        this.logger.logInfo(`Resetting pbjs state`);
        this.prebidInstance.requestSent = false;
        this.prebidInstance.adUnits = [];
        if (this.configuration.defaults?.granularity &&
            this.configuration.defaults.rubicon &&
            this.configuration.defaults.consentTimeout) {
            const prebidConfig = {
                priceGranularity: this.configuration.defaults.granularity,
                useBidCache: false,
                rubicon: this.configuration.defaults.rubicon,
                consentManagement: {
                    gdpr: {
                        cmpApi: PbjsConsentApi.IAB,
                        timeout: this.configuration.defaults.consentTimeout,
                        defaultGdprScope: true,
                    },
                },
                userSync: this.configuration.userSync,
            };
            // Remove if not defined
            if (this.configuration.userSync === undefined)
                delete prebidConfig.userSync;
            this.prebidInstance.setConfig(prebidConfig);
        }
    }
    async prebidPlacements(placements) {
        return new Promise((resolve) => {
            this.resetPrebidState();
            const adUnits = this.buildAdUnits(placements);
            this.logger.logInfo(`Enable Rubicon analytics`);
            this.prebidInstance.enableAnalytics({
                provider: "rubicon",
                options: {
                    accountId: 10101,
                    endpoint: "https://prebid-a.rubiconproject.com/event",
                },
            });
            this.logger.logInfo(`Prebid ${adUnits.length} placements`);
            this.prebidInstance.addAdUnits(adUnits);
            this.logger.logInfo(`Request bids from Prebid`);
            this.prebidInstance.requestBids({
                bidsBackHandler: () => {
                    this.logger.logInfo(`Prebid bids returned`);
                    this.prebidInstance.setTargetingForAst();
                    resolve();
                },
                timeout: this.configuration.defaults?.prebidTimeout ?? 800,
            });
        });
    }
}
export { PrebidService };
//# sourceMappingURL=prebid.js.map